import siteConfig from "../data/site-config.json";

export const SITE_URL = siteConfig.siteUrl.replace(/\/+$/, "");
export const SITE_NAME = siteConfig.siteName;

export function resolveSiteUrl(pathname = "") {
  const value = String(pathname || "").trim();

  if (!value) {
    return `${SITE_URL}/`;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  const normalizedPath = value.startsWith("/") ? value.slice(1) : value;
  return new URL(normalizedPath, `${SITE_URL}/`).toString();
}

export const SITE_LOGO = resolveSiteUrl(
  siteConfig.siteLogoPath || "/img/logo/mit-logo-w.webp",
);

// Every in-app route renders with a trailing slash (App.js redirects
// "/about-us" -> "/about-us/"), so internal links must already target that
// form — otherwise every click round-trips through a redirect and flashes.
export function toRoutePath(pathname = "/") {
  const value = String(pathname || "/");

  if (!value.startsWith("/") || /^\/\//.test(value)) {
    return value;
  }

  const [path, hash] = value.split("#");
  const [cleanPath, query] = path.split("?");
  const normalizedPath =
    cleanPath === "/" ? "/" : `${cleanPath.replace(/\/+$/, "")}/`;

  return `${normalizedPath}${query ? `?${query}` : ""}${hash ? `#${hash}` : ""}`;
}

export function normalizePathname(pathname = "/") {
  if (!pathname || pathname === "/") {
    return "/";
  }

  return pathname.replace(/\/+$/, "") || "/";
}

export function buildCanonicalUrl(pathname = "/") {
  const normalizedPathname = normalizePathname(pathname);
  return normalizedPathname === "/" ? `${SITE_URL}/` : `${SITE_URL}${normalizedPathname}/`;
}

export function resolveImageUrl(imagePath) {
  if (!imagePath) {
    return SITE_LOGO;
  }

  return resolveSiteUrl(imagePath);
}

export function createBreadcrumbSchema(pathname, title) {
  const normalizedPathname = normalizePathname(pathname);

  if (normalizedPathname === "/") {
    return null;
  }

  const segments = normalizedPathname.split("/").filter(Boolean);
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${SITE_URL}/`,
    },
  ];

  let currentPath = "";

  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === segments.length - 1;
    const label = isLast
      ? title.replace(new RegExp(`\\s+\\|\\s+${SITE_NAME}.*$`), "")
      : segment.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

    items.push({
      "@type": "ListItem",
      position: index + 2,
      name: label,
      item: buildCanonicalUrl(currentPath),
    });
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

export function createWebPageSchema({ path, title, description, type = "WebPage" }) {
  return {
    "@context": "https://schema.org",
    "@type": type,
    name: title.replace(new RegExp(`\\s+\\|\\s+${SITE_NAME}.*$`), ""),
    url: buildCanonicalUrl(path),
    description,
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: `${SITE_URL}/`,
    },
    about: {
      "@type": "Organization",
      name: SITE_NAME,
      url: `${SITE_URL}/`,
    },
  };
}
